import React from 'react';
import { motion } from 'framer-motion';
import Breadcrumbs from './Breadcrumbs';

/**
 * PageHero - inner-page banner (lighter than the homepage's animated hero).
 *
 * Pattern:
 *   - Soft sky mesh gradient background
 *   - Breadcrumbs at top
 *   - Optional eyebrow
 *   - Big page title (display font)
 *   - Optional subhead
 *   - Optional italic line (matches reference site for solution pages)
 *   - Optional children slot (for CTAs etc)
 */
const PageHero = ({
  eyebrow,
  title,
  titleAccent,
  subhead,
  italicLine,
  breadcrumbs,
  children,
}) => {
  const [titleHead, ...titleTail] = (title || '').split(' ');
  const titleRest = titleTail.join(' ');
  return (
    <section className="relative pt-28 pb-14 lg:pt-36 lg:pb-20 mesh-sky-bg overflow-hidden">
      {/* Decorative sky orbs */}
      <div className="absolute top-20 right-0 w-96 h-96 rounded-full bg-accent-soft/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-accent-soft/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-8">
        {breadcrumbs && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-6"
          >
            <Breadcrumbs items={breadcrumbs} />
          </motion.div>
        )}

        <div className="max-w-4xl">
          {eyebrow && (
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="eyebrow"
            >
              {eyebrow}
            </motion.p>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-[68px] text-text-primary tracking-[-0.02em] leading-[1.05] text-balance mb-4"
          >
            <span className="text-gradient-sky">{titleHead}</span>
            {titleRest ? ' ' + titleRest : ''}
            {titleAccent ? ' ' : ''}
            {titleAccent}
          </motion.h1>

          {italicLine && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="tagline-italic text-lg lg:text-xl mb-3"
            >
              {italicLine}
            </motion.p>
          )}

          {subhead && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="text-lg lg:text-xl text-text-secondary text-pretty max-w-2xl"
            >
              {subhead}
            </motion.p>
          )}

          {children && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-8"
            >
              {children}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PageHero;
